import React from "react";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Stack,
  Typography,
  Skeleton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
} from "recharts";
import {
  formatTrendChartAxisDate,
  formatTrendChartTooltipPeriodLabel,
  explorerAccentAt,
} from "./utils";
import i18n from "../../i18n";

export interface TrendChartConfig {
  title: string;
  dataKey: string;
  color: string;
  tooltipLabel: string;
  formatValue?: (value: number) => string;
  yAxisWidth?: number;
  allowDecimals?: boolean;
  reversed?: boolean;
  /** When set, this chart shows a skeleton even if the section is not loading. */
  isLoading?: boolean;
  /** Optional caption under title (e.g. dailyMetrics since date). */
  caption?: string;
  /** Icon shown before the title (e.g. platform mark). */
  titleIcon?: React.ReactNode;
}

interface TrendChartCardProps extends TrendChartConfig {
  data: Array<Record<string, any>>;
  median?: number;
  granularityTooltip: string;
  isLoading?: boolean;
}

const CHART_HEIGHT = 120;

// Chart area placeholder (matches ResponsiveContainer height)
const ChartSkeleton: React.FC = () => (
  <Box sx={{ width: "100%", height: CHART_HEIGHT }}>
    <Skeleton
      variant="rounded"
      width="100%"
      height="100%"
      sx={{ borderRadius: 1 }}
    />
  </Box>
);

const TrendChartCardComponent: React.FC<TrendChartCardProps> = ({
  title,
  caption,
  titleIcon,
  data,
  dataKey,
  median,
  color,
  tooltipLabel,
  granularityTooltip,
  formatValue,
  yAxisWidth = 35,
  allowDecimals = true,
  reversed = false,
  isLoading = false,
}) => {
  const theme = useTheme();

  const chartData = React.useMemo(
    () =>
      data.map((row) => {
        const value = row[dataKey];
        return {
          ...row,
          [dataKey]: value == null ? undefined : value,
        };
      }),
    [data, dataKey],
  );

  const gradientId = `gradient-${dataKey}-${color.replace("#", "")}`;

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2,
        overflow: "hidden",
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {titleIcon ? (
            <Box sx={{ display: "flex", lineHeight: 0, flexShrink: 0 }}>
              {titleIcon}
            </Box>
          ) : null}
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {title}
          </Typography>
        </Stack>
        {caption ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {caption}
          </Typography>
        ) : null}
      </Box>

      {isLoading ? (
        <ChartSkeleton />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <AreaChart
            data={chartData}
            margin={{ top: 5, right: 5, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient
                id={gradientId}
                x1="0"
                y1={reversed ? "1" : "0"}
                x2="0"
                y2={reversed ? "0" : "1"}
              >
                <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={alpha(theme.palette.divider, 0.15)}
              vertical={false}
              horizontal={true}
            />
            <XAxis
              dataKey="weekStart"
              tickFormatter={formatTrendChartAxisDate}
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              tickLine={false}
              axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              tickLine={false}
              axisLine={false}
              width={yAxisWidth}
              allowDecimals={allowDecimals}
              tickFormatter={formatValue}
              reversed={reversed}
            />
            <RechartsTooltip
              formatter={(value) => {
                if (value == null || typeof value !== "number") {
                  return ["—", tooltipLabel];
                }
                return [
                  formatValue ? formatValue(value) : value,
                  tooltipLabel,
                ];
              }}
              labelFormatter={(label) =>
                formatTrendChartTooltipPeriodLabel(
                  label as string,
                  granularityTooltip,
                )
              }
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 12,
              }}
            />
            {median !== undefined && (
              <ReferenceLine
                y={median}
                stroke={color}
                strokeDasharray="4 4"
                strokeOpacity={0.4}
              />
            )}
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: color }}
              baseValue={reversed ? "dataMax" : "dataMin"}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

export const TrendChartCard = React.memo(TrendChartCardComponent);

export type TrendGranularity = "daily" | "weekly";

/** Granularity used for period totals (summary cards); totals don't depend on granularity. */
export const SUMMARY_TREND_GRANULARITY: TrendGranularity = "weekly";

/** Persisted state may still hold "monthly" from before it was removed. */
export function normalizeTrendGranularity(value: unknown): TrendGranularity {
  return value === "daily" ? "daily" : "weekly";
}

const AUTO_DAILY_MAX_DAYS = 7;

/** Default granularity for a YYYY-MM-DD range (inclusive): daily up to a week, weekly beyond. */
export function autoTrendGranularity(startDate: string, endDate: string): TrendGranularity {
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return "weekly";
  const days = Math.round(Math.abs(end - start) / 86_400_000) + 1;
  return days <= AUTO_DAILY_MAX_DAYS ? "daily" : "weekly";
}

export const GRANULARITY_LABELS: Record<
  TrendGranularity,
  { title: string; tooltip: string }
> = {
  get daily() {
    return {
      title: i18n.t("explorer:granularity.dailyTitle"),
      tooltip: i18n.t("explorer:granularity.day"),
    };
  },
  get weekly() {
    return {
      title: i18n.t("explorer:granularity.weeklyTitle"),
      tooltip: i18n.t("explorer:granularity.week"),
    };
  },
};

/** Line color comes from the chart's position (see `EXPLORER_ACCENT_SEQUENCE`). */
export type TrendSectionChart = Omit<TrendChartConfig, "color"> & {
  median?: number;
};

interface TrendSectionProps {
  granularity: TrendGranularity;
  onGranularityChange: (granularity: TrendGranularity) => void;
  data: Array<Record<string, any>>;
  charts: TrendSectionChart[];
  isLoading?: boolean;
  calculateMedian?: (values: number[]) => number;
}

export const TrendSection: React.FC<TrendSectionProps> = ({
  granularity,
  onGranularityChange,
  data,
  charts,
  isLoading = false,
  calculateMedian,
}) => {
  const { t } = useTranslation("explorer");
  const chartsWithMedians = React.useMemo(() => {
    return charts.map((chart) => {
      if (chart.median !== undefined) {
        return chart;
      }
      if (calculateMedian && data.length > 0) {
        const values = data
          .map((d) => d[chart.dataKey] as number)
          .filter((v) => typeof v === "number");
        return { ...chart, median: calculateMedian(values) };
      }
      return chart;
    });
  }, [charts, data, calculateMedian]);

  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h2">
          {GRANULARITY_LABELS[granularity].title}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("viewBy")}</InputLabel>
          <Select
            value={granularity}
            label={t("viewBy")}
            onChange={(e) =>
              onGranularityChange(e.target.value as TrendGranularity)
            }
          >
            <MenuItem value="daily">{t("daily")}</MenuItem>
            <MenuItem value="weekly">{t("weekly")}</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {chartsWithMedians.map((chart, index) => (
          <TrendChartCard
            key={chart.dataKey || index}
            {...chart}
            color={explorerAccentAt(index)}
            data={data}
            granularityTooltip={GRANULARITY_LABELS[granularity].tooltip}
            isLoading={chart.isLoading ?? isLoading}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default TrendChartCard;
